import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import RemediationTriggerList from "./RemediationTriggerList";
import { Shield, ChevronDown, ChevronRight, Settings, AlertTriangle, FlaskConical, ClipboardList, Search } from "lucide-react";
import Pagination from "../components/Pagination";
import { usePageSize } from "../hooks/usePageSize";
import { useRegions } from "../contexts/RegionContext";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import { useAuth } from "../contexts/AuthContext";
import { useVendorPerformanceScorecard } from "../hooks/useVendorPerformanceScorecard";
import VendorPerformanceScorecardTab from "./VendorPerformanceScorecardTab";
import VendorPerformanceReviewModal from "./VendorPerformanceReviewModal";
import Navigation from "../components/Navigation";
import { PINNED_PANEL_TOP } from "../components/PinnedNavigation";
import LoadingSpinner from "../components/LoadingSpinner";
import Card from "../components/Card";
import Button from "../components/Buttons";
import FinancialImpactSection from "../components/FinancialImpactSection";
import AssignRiskModal from "../components/AssignRiskModal";
import BulkAssignRiskModal from "../components/BulkAssignRiskModal";
import Modal from "../components/Modal";
import ActivityLogTab from "./ActivityLogTab";
import VendorControlMapping from "./VendorControlMapping";
import VendorAiRiskModule from "./VendorAiRiskModule";
import VendorScoreTrend from "./VendorScoreTrend";
import VendorSingleIntervention from "./VendorSingleIntervention";
import VendorImpactMetrics from "./VendorImpactMetrics";
import VendorAnomalyBadge from "./VendorAnomalyBadge";
import VendorExternalThreats from "./VendorExternalThreats";
import VendorDocumentsTab from "./VendorDocumentsTab";
import VendorAiNarrative from "./VendorAiNarrative";
import VendorWhatIfExplorer from "./VendorWhatIfExplorer";
import ReactSpeedometer from "react-d3-speedometer";
import { RISK_GAUGE_SEGMENT_STOPS, RISK_GAUGE_SEGMENT_COLORS, getComplianceTextColor, getComplianceBarColor } from '../utils/riskColors';
import {
    RISK_STATUS_FILTER_DEFAULT, applyRiskStatusFilter, countActiveRisks,
    isActiveRisk, RiskStatusFilter, RiskStatusBadge,
} from "./shared/riskStatus";
import AssessmentContextHeader from "./shared/AssessmentContextHeader";
import { VulnerabilityDistributionCard, PredictiveRiskBars, RiskSourceBreakdownCard, RiskScoreFootnote, ControlEffectivenessCard, ResidualRiskCard } from "./RiskScoreCards";
import { severityPostureScore, severityPostureLevel, computeOrganizationAnalytics } from '../utils/riskScoringEngine';
import { parseRemediationSteps } from '../utils/remediationSteps';

const RISK_TYPE_LABELS = {
    "BusinessUnit": "Business Units",
    "BusinessUnits": "Business Units",
    "Enterprise": "Enterprise",
    "DarkWeb": "Dark Web",
    "Ransomware": "Ransomware",
    "AI": "AI",
    "TPRM": "TPRM",
    "ExternalSurfaceScan": "External Surface",
    "External": "External",
    "GRC": "GRC",
};

// 90-day material-incident probability bands. Thresholds sit far below generic risk-score
// bands on purpose: an ~18% chance of a *material incident within 90 days* is elevated, not
// "low". Keep the cut points in sync with INCIDENT_BAND_TOOLTIP below.
const INCIDENT_BANDS = [
    { max: 5,        label: "Low",      text: "text-green-600",  badge: "bg-green-100 text-green-700",   bar: "bg-green-500" },
    { max: 15,       label: "Moderate", text: "text-amber-500",  badge: "bg-amber-100 text-amber-700",   bar: "bg-amber-400" },
    { max: 30,       label: "Elevated", text: "text-orange-500", badge: "bg-orange-100 text-orange-700", bar: "bg-orange-500" },
    { max: Infinity, label: "High",     text: "text-red-600",    badge: "bg-red-100 text-red-700",       bar: "bg-red-500" },
];
const getIncidentBand = (p) => INCIDENT_BANDS.find(b => (p ?? 0) < b.max) || INCIDENT_BANDS[INCIDENT_BANDS.length - 1];
const INCIDENT_BAND_TOOLTIP =
    "Estimated probability that at least one material security incident occurs at this vendor within the next 90 days " +
    "(Monte Carlo across all risk types). Distinct from the Risk Score, which reflects current control posture and finding severity. " +
    "Bands: <5% Low · 5–15% Moderate · 15–30% Elevated · >30% High.";
const RISK_SCORE_MODEL_MAX = 97; // MonetaryImpactEngine.MaxRiskScore — a maxed-out score reads 97, never 100.
const CURRENT_SCORE_CI_TOOLTIP =
    "The score is this vendor's latest observed daily snapshot. The range is a modeled 90% band derived from recent " +
    "day-to-day volatility (±1.645σ over the last 14 days) — it shows how much the score has been moving, not measurement error.";
const TRAJECTORY_TOOLTIP =
    "Projection fits a linear trend to recent daily scores. The Risk Score is severity-anchored — the worst active finding " +
    `severity sets the band, so it can jump when severity changes even if the finding count doesn't. Scores are capped at the ` +
    `model maximum of ${RISK_SCORE_MODEL_MAX}%; a forecast that flattens at ${RISK_SCORE_MODEL_MAX}% has reached that ceiling.`;

const FAIR_RISK_TYPE_INFO = {
    "DB": { name: "Data Breach", description: "Data breach, data leak, data loss, exfiltration, or exposure events.", businessImpact: "data breach findings represent direct exposure to breach notification costs, regulatory fines, and customer churn — costs that land on your organization even when the breach originates at the vendor" },
    "RW": { name: "Ransomware", description: "Ransomware and malware-related threats.", businessImpact: "ransomware exposure could result in operational shutdown, data loss, and ransom payments — and if the vendor's compromise propagates to your environment, you bear those costs" },
    "RA": { name: "Unauthorized Access", description: "Remote access, credential compromise, privilege escalation, or account takeover.", businessImpact: "access control weaknesses are the most likely path through which your data or systems get compromised, since attackers who control this vendor's credentials can pivot directly into your environment" },
    "BEC": { name: "Business Email Compromise", description: "Phishing, email compromise, and social engineering attacks.", businessImpact: "email compromise risks can lead to fraudulent wire transfers, credential theft, and targeted phishing attacks launched against your employees using the vendor's trusted identity" },
    "OO": { name: "Operational Outage", description: "Service availability disruptions and cloud outages.", businessImpact: "any outage at this vendor directly disrupts your operations and revenue — the more dependent your processes are on this vendor, the larger the financial impact" },
    "IP": { name: "IP Theft", description: "Intellectual property theft, trade secret exposure, or insider data theft.", businessImpact: "IP theft risks expose your competitive advantage and proprietary data to unauthorized parties through the vendor's environment" },
    "SC": { name: "Supply Chain", description: "Third-party and supply chain risk exposure.", businessImpact: "supply chain risks multiply your exposure by creating attack paths through this vendor's own third-party ecosystem — their vendors become your indirect risk" },
    "DDOS": { name: "DDoS", description: "Distributed denial-of-service attacks.", businessImpact: "denial-of-service risks can take this vendor's services offline without warning, disrupting your operations and any revenue that depends on their availability" },
    "AI": { name: "AI System Risk", description: "Risks specific to AI and machine learning systems.", businessImpact: "AI system risks can result in manipulated outputs, model poisoning, or data leakage that affects your decisions or your customers' data" },
    "FF": { name: "Financial Fraud", description: "Fraud and financial manipulation threats.", businessImpact: "financial fraud risks can result in unauthorized transactions, accounting manipulation, or direct monetary losses that flow through to your organization" },
    "CM": { name: "Cloud Misconfiguration", description: "Cloud infrastructure and configuration management gaps.", businessImpact: "cloud misconfiguration gaps create readily exploitable attack surfaces — exposed storage buckets, weak IAM policies, and unsecured APIs that can give attackers direct access to your data" },
    "IT": { name: "Insider Threat", description: "Threats originating from internal actors.", businessImpact: "insider threat risks from the vendor's own employees can bypass technical controls entirely, giving malicious actors privileged access to data or systems that touch your environment" },
    "TPE": { name: "Third-Party Exposure", description: "Exposure through third-party relationships and integrations.", businessImpact: "third-party exposure means this vendor's own vendor relationships expand your attack surface — a breach anywhere in their supply chain can reach your data through their integrations" },
};

const buildCrisisNarrative = ({ financialSummary, analytics, risks, vendor }) => {
    const paragraphs = [];
    const vendorName = vendor?.vendorName || "This vendor";
    const eal = financialSummary?.totalExpectedAnnualLoss;
    const incomplete = financialSummary?.importanceBreakdown?.hasMissingInputs;
    const rtBreakdown = financialSummary?.riskTypeBreakdown;

    // Para 1 — Why this is YOUR financial risk
    const ds = financialSummary?.importanceBreakdown?.dataSensitivity;
    const as_ = financialSummary?.importanceBreakdown?.accessScope;
    if (incomplete) {
        paragraphs.push(
            `${vendorName}'s security findings translate directly into financial exposure for your organization — to the extent that this vendor accesses your data, integrates with your systems, or supports your revenue. ` +
            `The more access and dependency, the more their risk becomes your liability: through regulatory accountability for vendor-held data, operational disruption if they fail, and reputational impact from incidents customers attribute to you.`
        );
    } else if (ds && as_) {
        paragraphs.push(
            `${vendorName}'s security findings translate directly into financial exposure for your organization. ` +
            `With ${ds} data sensitivity and ${as_} access scope, when this vendor is compromised your organization absorbs the downstream costs — ` +
            `regulatory fines, breach notification, operational disruption, and reputational damage — even though the vulnerabilities originated at the vendor.`
        );
    } else {
        paragraphs.push(
            `${vendorName}'s security findings translate directly into financial exposure for your organization. ` +
            `When a vendor with this level of access and integration is compromised, your organization absorbs the downstream costs — ` +
            `regulatory fines, breach notification, operational disruption, and reputational damage — even though the vulnerabilities originated at the vendor.`
        );
    }

    // Para 2 — What the numbers project
    if (eal) {
        const severe = financialSummary?.scenarioBands?.severe;
        let para = `In a typical year, projected annual loss to your organization from this vendor is ${formatImpact(eal)}.`;
        if (severe) {
            para += ` In a stress scenario where multiple findings materialize together, exposure reaches ${formatImpact(severe)} — the planning figure for insurance sizing and board reporting.`;
        }
        if (rtBreakdown?.length > 0) {
            const sorted = [...rtBreakdown].sort((a, b) => b.expectedAnnualLoss - a.expectedAnnualLoss);
            const top = sorted[0];
            const pct = ((top.expectedAnnualLoss / eal) * 100).toFixed(0);
            const info = FAIR_RISK_TYPE_INFO[top.riskType];
            const typeName = info?.name || top.riskType;
            para += ` The single largest driver is ${typeName} at ${pct}% (${formatImpact(top.expectedAnnualLoss)})`;
            if (info?.businessImpact) {
                para += ` — ${info.businessImpact}`;
            }
            para += ".";
            if (sorted.length >= 2) {
                const second = sorted[1];
                const pct2 = ((second.expectedAnnualLoss / eal) * 100).toFixed(0);
                const info2 = FAIR_RISK_TYPE_INFO[second.riskType];
                const typeName2 = info2?.name || second.riskType;
                para += ` ${typeName2} contributes an additional ${pct2}% (${formatImpact(second.expectedAnnualLoss)}).`;
            }
        }
        paragraphs.push(para);
    }

    // Para 3 — Provisional warning (only when classification is incomplete)
    if (incomplete) {
        paragraphs.push(
            `These estimates are provisional — vendor classification is incomplete. The model is currently using industry averages for data sensitivity, access scope, and revenue dependency. ` +
            `Completing those three fields in vendor settings will either tighten this estimate or move it materially: ` +
            `if this vendor has minimal data access and limited integration, actual exposure is lower; if they handle regulated data or critical operations, it could be significantly higher.`
        );
    }

    // Para 4 — How to reduce the number
    if (eal) {
        const critical = analytics?.risk_distribution?.CRITICAL || 0;
        let para = incomplete
            ? `Two levers reduce this number: complete the vendor classification to replace assumptions with facts, and push ${vendorName} to remediate its highest-severity findings`
            : `To reduce this exposure, push ${vendorName} to remediate its highest-severity findings`;
        if (rtBreakdown?.length > 0) {
            const sorted = [...rtBreakdown].sort((a, b) => b.expectedAnnualLoss - a.expectedAnnualLoss);
            const top = sorted[0];
            const info = FAIR_RISK_TYPE_INFO[top.riskType];
            if (info) {
                para += `, starting with ${info.name} risks, which drive the largest share of projected loss`;
            }
        }
        if (critical > 0) {
            para += `. There ${critical === 1 ? "is" : "are"} ${critical} critical finding${critical !== 1 ? "s" : ""} that should be addressed first — each remediated finding directly reduces the projected EAL`;
        }
        para += ".";
        paragraphs.push(para);
    }

    return paragraphs;
};

const getRiskColor = (level) => {
    switch (level) {
        case "CRITICAL": return "bg-red-100 text-red-700 border-red-500";
        case "HIGH": return "bg-orange-100 text-orange-800 border-orange-500";
        case "MEDIUM": return "bg-yellow-100 text-yellow-800 border-yellow-500";
        case "LOW": return "bg-green-100 text-green-800 border-green-500";
        default: return "bg-gray-100 text-gray-800 border-gray-500";
    }
};

const getRiskBarColor = (level) => {
    switch (level) {
        case "CRITICAL": return "bg-red-700";
        case "HIGH": return "bg-orange-500";
        case "MEDIUM": return "bg-yellow-500";
        case "LOW": return "bg-green-500";
        default: return "bg-gray-400";
    }
};

const getRiskBadgeColor = (type) => {
    switch (type) {
        case "TPRM": return "bg-green-100 text-green-800";
        case "DarkWeb": return "bg-purple-100 text-purple-800";
        case "Enterprise": return "bg-blue-100 text-blue-800";
        case "Ransomware": return "bg-red-100 text-red-800";
        case "AI": return "bg-cyan-100 text-cyan-800";
        case "BusinessUnit": return "bg-amber-100 text-amber-800";
        case "ExternalSurfaceScan": return "bg-teal-100 text-teal-800";
        default: return "bg-gray-100 text-gray-800";
    }
};

// Always millions, never a raw dollar figure below $1M \u2014 matches every sibling *AssessmentRisksView.js
// (TPRM's own included, fixed in PR 554). This file had its own independent copy that never got that
// fix, so a risk's "Engine-Calculated Range" could show one endpoint raw and the other in millions
// (e.g. "$64,987 - $1.73M+") whenever the range straddled the old $1M threshold.
const formatImpact = (val) => {
    if (val == null) return "\u2014";
    return `$${(Number(val) / 1_000_000).toFixed(3)}M`;
};

// A single mapped value that's a scoping note rather than a real control code (e.g. "N/A - out of
// scope for the US Bulk Electric System / NERC CIP assessment") — never rendered as a control chip,
// even when it rides alongside real control codes in the same framework's mapping.
const isNotApplicableEntry = (c) => typeof c === "string" && /^\s*n\/a\b/i.test(c);

const isFrameworkNotApplicable = (controls) => {
  // frameworkImpact values arrive as string[] (see backend/Extensions/FrameworkImpactConverter.cs —
  // Dictionary<string, List<string>>, even for a single value), so a plain typeof === "string"
  // check never matches and every N/A note slips through as a 1-element array.
  const list = Array.isArray(controls) ? controls : typeof controls === "string" ? [controls] : [];
  return list.length > 0 && list.every(isNotApplicableEntry);
};

const FrameworkCard = ({ title, controls = "", color }) => {
  const [expanded, setExpanded] = useState(false);
  const bgColors = {
    blue: "bg-blue-50 border-blue-300",
    green: "bg-green-50 border-green-300",
    purple: "bg-purple-50 border-purple-300",
    orange: "bg-orange-50 border-orange-300",
    red: "bg-red-50 border-red-300",
    pink: "bg-pink-50 border-pink-300",
    indigo: "bg-indigo-50 border-indigo-300",
    yellow: "bg-yellow-50 border-yellow-300",
    teal: "bg-teal-50 border-teal-300",
    cyan: "bg-cyan-50 border-cyan-300",
    gray: "bg-gray-100 border-gray-300",
    emerald: "bg-emerald-50 border-emerald-300",
    violet: "bg-violet-50 border-violet-300",
    rose: "bg-rose-50 border-rose-300",
    amber: "bg-amber-50 border-amber-300",
    sky: "bg-sky-50 border-sky-300",
  };

  const controlList = (Array.isArray(controls)
    ? controls
    : typeof controls === "string"
      ? controls.split(";").map(c => c.trim()).filter(Boolean)
      : []
  ).filter(c => !isNotApplicableEntry(c));

  const visibleControls = expanded ? controlList : controlList.slice(0, 2);
  const hiddenCount = controlList.length - visibleControls.length;

  return (
    <div className={`min-w-[85px] px-2 py-2 rounded-md border ${bgColors[color] || "bg-gray-50 border-gray-300"}`}>
      <div className="text-[9px] font-semibold text-center uppercase leading-tight">
        {title}
      </div>
      <div className="flex flex-wrap gap-1 justify-center mt-1">
        {visibleControls.map((c, i) => (
          <span key={i} className="px-1 py-[1px] bg-white border rounded text-[8px]">
            {c}
          </span>
        ))}
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="text-[8px] text-gray-400 hover:text-gray-700 hover:underline cursor-pointer"
            title={`Show ${hiddenCount} more control${hiddenCount === 1 ? "" : "s"}`}
          >
            +{hiddenCount}
          </button>
        )}
        {expanded && controlList.length > 2 && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="text-[8px] text-gray-400 hover:text-gray-700 hover:underline cursor-pointer"
          >
            Show less
          </button>
        )}
      </div>
    </div>
  );
};

const getFrameworkColor = (framework) => {
  const colors = {
    "SOC 2": "blue",
    "NIST CSF 2.0": "green",
    "ISO 27001:2022": "purple",
    "CIS Controls v8": "orange",
    "PCI DSS v4": "red",
    "HIPAA": "pink",
    "GDPR": "indigo",
    "DORA": "purple",
    "FedRAMP / 800-53": "yellow",
    "NIST 800-171": "teal",
    "CMMC L2": "cyan",
    "HITRUST CSF": "gray",
    "NIST AI RMF": "emerald",
    "ISO 42001": "violet",
    "OWASP LLM Top 10": "rose",
    "MITRE ATLAS": "amber",
    "CSA AI Controls": "sky",
  };
  return colors[framework] || "blue";
};

const VendorDashboard = () => {
    const { vendorId } = useParams();
    const navigate = useNavigate();
    const apiCall = useApi();
    const { canManageOrganizationRisks, canManageDeal, canManageBusinessUnit, canUpdateRiskViaAssignment, canCreateVendor } = useRBAC();
    const { user: currentUser } = useAuth();

    const [vendor, setVendor] = useState(null);
    const [risks, setRisks] = useState([]);
    const [loading, setLoading] = useState(true);
    // Backend gates every per-vendor endpoint on deal/BU association (CanReadVendor) — a 403/404
    // on the core fetches means this vendor isn't in the caller's scope, not a transient error.
    const [accessDenied, setAccessDenied] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = usePageSize('vendorRisks');
    const listTopRef = useRef(null);

    const [expandedCards, setExpandedCards] = useState({});
    const [riskActiveTabs, setRiskActiveTabs] = useState({});
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedRisk, setSelectedRisk] = useState(null);
    const [selectedRiskIds, setSelectedRiskIds] = useState(new Set());
    const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
    const [dashboardTab, setDashboardTab] = useState("risks"); // risks | financial | controls
    const [riskTypeFilter, setRiskTypeFilter] = useState("");
    const [vectorFilter, setVectorFilter] = useState(""); // aiRiskType code e.g. "DB","RA"
    const [statusFilter, setStatusFilter] = useState(RISK_STATUS_FILTER_DEFAULT);
    const [searchTerm, setSearchTerm] = useState("");
    const [financialSummary, setFinancialSummary] = useState(null);
    const [showClassifyModal, setShowClassifyModal] = useState(false);
    const [classifyForm, setClassifyForm] = useState({
        dataSensitivity: "", accessScope: "", revenueDependency: "",
        vendorTier: "", tierOverrideReason: "", autoCalculate: true
    });
    const [classifySaving, setClassifySaving] = useState(false);
    const [showWhatIf, setShowWhatIf] = useState(false);
    const [showPerfReview, setShowPerfReview] = useState(false);
    const [anomalyData, setAnomalyData] = useState(null);
    const [showFinancialMethodology, setShowFinancialMethodology] = useState(false);
    const [frameworkReadiness, setFrameworkReadiness] = useState(null);
    const [riskVectors, setRiskVectors] = useState(null);
    const [riskVectorsLoading, setRiskVectorsLoading] = useState(true);
    const [showAllVectors, setShowAllVectors] = useState(false);
    const [deteriorationForecast, setDeteriorationForecast] = useState(null);
    const [predictiveRisk, setPredictiveRisk] = useState({ current: 0, day45: 0, day90: 0, day180: 0, escalating180: 0 });
    // Yes/Partially/No scoring of the vendor's newest (preferring completed) TPRM assessment —
    // feeds the Control Effectiveness card, same as on the TPRM assessment risks page.
    const [assessmentScoring, setAssessmentScoring] = useState(null);
    const [loadingPredictive, setLoadingPredictive] = useState(true);

    // Monte Carlo is computed LIVE (never persisted) and shared across the impact metrics + AI
    // narrative panels, so a single Recalculate refreshes every derived figure on the page.
    // Historical snapshots are written only by the nightly worker.
    const [mcData, setMcData] = useState(null);
    const [mcLoading, setMcLoading] = useState(true);
    const [mcRecalculating, setMcRecalculating] = useState(false);

    const handleAnomalyData = useCallback((data) => setAnomalyData(data), []);

    // Context header (Region / Org / Vendor)
    const selectedOrg = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
    const regions = useRegions();
    const orgName = selectedOrg?.organizationName;
    const contextRegionId = vendor?.regionId ?? selectedOrg?.regionId;
    const regionName = vendor?.regionName
        || (contextRegionId ? regions?.find(r => r.regionId === contextRegionId)?.regionName : null);

    const canManageThisRisk = (risk) =>
        canManageOrganizationRisks()
        || (risk.dealId && canManageDeal(risk.dealId))
        || (risk.businessUnitId && canManageBusinessUnit(risk.businessUnitId))
        || canUpdateRiskViaAssignment(risk.assignedToUserId);

    const canManagePerfScorecard = !!vendor?.organizationId && canCreateVendor(vendor.organizationId);
    const perfScorecard = useVendorPerformanceScorecard(vendorId, canManagePerfScorecard, currentUser?.id);

    const activeRisks = useMemo(() => risks.filter(isActiveRisk), [risks]);

    // Breakdown of the vendor's ACTIVE risks by source: the vendor's own TPRM risks vs. everything
    // else (External feeds plus any cross-listed Enterprise/AI/GRC risks) bucketed as External.
    const riskSources = useMemo(() => {
        const tprm = activeRisks.filter(r => r.riskType === 'TPRM').length;
        return [
            { label: 'TPRM', count: tprm, dot: 'bg-purple-600', text: 'text-purple-700' },
            { label: 'External', count: activeRisks.length - tprm, dot: 'bg-cyan-600', text: 'text-cyan-700' },
        ];
    }, [activeRisks]);

    const filteredRisks = useMemo(() => {
        let result = applyRiskStatusFilter(risks, statusFilter);
        if (riskTypeFilter)
            result = result.filter(r => (r.riskType || "Unknown") === riskTypeFilter);
        if (vectorFilter)
            result = result.filter(r => {
                if (!r.aiRiskType) return false;
                const code = r.aiRiskType.trim().toUpperCase();
                return (code === "DD" ? "DDOS" : code) === vectorFilter;
            });
        // Same match as the risk search in the assessment risk views (TPRMAssessmentRisksView).
        if (searchTerm) {
            const t = searchTerm.toLowerCase();
            result = result.filter(r =>
                (r.riskTitle || "").toLowerCase().includes(t) ||
                (r.riskDescription || "").toLowerCase().includes(t) ||
                (r.riskCategory || "").toLowerCase().includes(t) ||
                (r.aiRiskType || "").toLowerCase().includes(t)
            );
        }
        return result;
    }, [risks, riskTypeFilter, vectorFilter, statusFilter, searchTerm]);

    useEffect(() => { setPage(1); }, [risks, riskTypeFilter, vectorFilter, statusFilter, searchTerm]);

    const totalPages = Math.max(1, Math.ceil(filteredRisks.length / pageSize));
    const pagedRisks = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredRisks.slice(start, start + pageSize);
    }, [filteredRisks, page, pageSize]);

    // Compute analytics from active risks (completed ones no longer contribute to scores)
    const analytics = useMemo(() => {
        if (!activeRisks.length) return null;
        const counts = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 };
        activeRisks.forEach(r => {
            const sev = (r.riskSeverity || "").toUpperCase();
            if (counts.hasOwnProperty(sev)) counts[sev]++;
        });
        const score = severityPostureScore(counts);
        const level = severityPostureLevel(score);

        // Full analytics via the shared v2 engine so the Control Effectiveness and Residual Risk
        // cards (same cards as the TPRM assessment risks page) get their residual_risk /
        // score_breakdown / control_effectiveness / ce_breakdown fields. The vendor's all-risks
        // rows are RiskDetailsDto-shaped (riskSeverity, no risk_severity), so map to the engine's
        // field names rather than duplicating its scoring logic here.
        const engineRisks = risks.map(r => ({
            id: r.id,
            risk_severity: r.riskSeverity,
            riskCategory: r.riskCategory || r.riskType,
            isRemediationCompleted: !isActiveRisk(r),
        }));
        const computed = computeOrganizationAnalytics(engineRisks, [], assessmentScoring);

        return {
            ...computed,
            // No TPRM assessment => no control evidence; null makes ControlEffectivenessCard render
            // "Assessment Not Started" instead of scoring the vendor 0% off its non-TPRM risks.
            control_effectiveness: assessmentScoring ? computed.control_effectiveness : null,
            // Preserve the pre-existing gauge inputs exactly as before (same posture formula, but
            // keep these authoritative so this change cannot shift the Vendor Risk Score display).
            overall_risk_score: score,
            overall_RiskLevel: level,
            total_risks: risks.length,
            active_risks: activeRisks.length,
            remediated_count: Math.max(0, risks.length - activeRisks.length),
            risk_distribution: counts,
        };
    }, [activeRisks, risks, assessmentScoring]);

    // Compute risk type breakdown (all risks, so completed-only sources stay filterable)
    const riskTypeBreakdown = useMemo(() => {
        const breakdown = {};
        risks.forEach(r => {
            const type = r.riskType || "Unknown";
            breakdown[type] = (breakdown[type] || 0) + 1;
        });
        return breakdown;
    }, [risks]);

    const crisisNarrative = useMemo(() => {
        if (!financialSummary || !financialSummary.totalExpectedAnnualLoss) return [];
        return buildCrisisNarrative({ financialSummary, analytics, risks, vendor });
    }, [financialSummary, analytics, risks, vendor]);

    useEffect(() => {
        fetchVendorData();
        fetchMc();
    }, [vendorId]);

    // Live Monte Carlo compute — auto-runs on load. The backend serves a cached snapshot if one was
    // computed within the last 15 minutes, so rapid re-visits don't re-run the simulation.
    const fetchMc = async (force = false) => {
        try {
            setMcLoading(true);
            const result = await apiCall("GET", `/TPRMVendor/${vendorId}/monte-carlo/live${force ? "?force=true" : ""}`);
            setMcData(result);
        } catch {
            // MC data optional — panels degrade gracefully
        } finally {
            setMcLoading(false);
        }
    };

    // Recalculate forces a fresh compute (bypassing the 15-minute cache) and refreshes every panel
    // that consumes MC data — impact metrics AND the AI narrative's underlying data points.
    const handleRecalculateMc = async () => {
        try {
            setMcRecalculating(true);
            await fetchMc(true);
        } finally {
            setMcRecalculating(false);
        }
    };

    const fetchVendorData = async () => {
        try {
            const [vendorData, risksData, financialData, readinessData, riskStatusData, forecastData, predData, vendorAssessments] = await Promise.all([
                apiCall("GET", `/TPRMVendor/${vendorId}`),
                apiCall("GET", `/TPRMVendor/${vendorId}/all-risks`),
                apiCall("GET", `/TPRMVendor/${vendorId}/financial-summary`).catch(() => null),
                apiCall("GET", `/TPRMVendor/${vendorId}/framework-readiness`).catch(() => null),
                apiCall("GET", `/TPRMVendor/${vendorId}/risk-status`).catch(() => null),
                apiCall("GET", `/TPRMVendor/${vendorId}/deterioration-forecast`).catch(() => null),
                apiCall("GET", `/TPRMVendor/${vendorId}/predictive-risk`).catch(() => null),
                apiCall("GET", `/TPRMAssessments/vendor?vendorId=${vendorId}`).catch(() => []),
            ]);

            // Control Effectiveness needs the Yes/Partially/No scoring of this vendor's TPRM
            // assessment — prefer the newest completed one (its answers are final), falling back
            // to the newest overall so an in-progress assessment still shows a live CE.
            const assessmentsArr = Array.isArray(vendorAssessments) ? vendorAssessments : [];
            const byNewest = [...assessmentsArr].sort((a, b) => (b.tprmAssessmentID ?? 0) - (a.tprmAssessmentID ?? 0));
            const scoringSource = byNewest.find(a => (a.tprmStatus || '').toLowerCase() === 'completed') ?? byNewest[0];
            setAssessmentScoring(scoringSource
                ? await apiCall("GET", `/TPRMAssessments/${scoringSource.tprmAssessmentID}/scoring`).catch(() => null)
                : null);
            setVendor(vendorData);
            // Keep all risks in state — the status filter (default: active) handles completed ones
            setRisks(Array.isArray(risksData) ? risksData : []);
            setFinancialSummary(financialData);
            setFrameworkReadiness(readinessData);
            setRiskVectors(riskStatusData?.risk_status || {});
            setDeteriorationForecast(forecastData || null);
            setPredictiveRisk({
                current: predData?.currentScore || 0,
                day45: predData?.predictive45DayScore || 0,
                day90: predData?.predictive90DayScore || 0,
                day180: predData?.predictive180DayScore || 0,
                escalating180: predData?.escalating180Count || 0,
            });
            if (vendorData) {
                setClassifyForm({
                    dataSensitivity: vendorData.dataSensitivity || "",
                    accessScope: vendorData.accessScope || "",
                    revenueDependency: vendorData.revenueDependency || "",
                    vendorTier: vendorData.vendorTier?.toString() || "",
                    tierOverrideReason: (vendorData.tierOverrideReason === "Auto-calculated" ? "" : vendorData.tierOverrideReason) || "",
                    autoCalculate: !vendorData.vendorTier,
                });
            }
        } catch (err) {
            if (err?.response?.status === 403 || err?.response?.status === 404) {
                setAccessDenied(true);
            }
        } finally {
            setLoading(false);
            setRiskVectorsLoading(false);
            setLoadingPredictive(false);
        }
    };

    const toggleCard = (id) => setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));

    const toggleRiskSelection = (riskId) =>
        setSelectedRiskIds(prev => {
            const next = new Set(prev);
            next.has(riskId) ? next.delete(riskId) : next.add(riskId);
            return next;
        });

    const toggleSelectAll = () =>
        setSelectedRiskIds(prev =>
            prev.size === filteredRisks.length && filteredRisks.length > 0
                ? new Set()
                : new Set(filteredRisks.map(r => r.id))
        );

    const getRiskAssignEndpoint = (risk) => {
        // All risks are mutated via the unified controller (GUID id).
        return `/unifiedrisks/${risk.id}/assign`;
    };

    const handleOpenAssignModal = (risk) => {
        setSelectedRisk(risk);
        setShowAssignModal(true);
    };

    if (loading) return <LoadingSpinner />;

    if (accessDenied) {
        // The scorecards endpoint has an additive reviewer path: assigned reviewers outside the
        // vendor's deal/BU scope get their own scorecards back even though every other vendor
        // fetch 403s. A non-empty result means "reviewer-only view", not "no access".
        if (perfScorecard.loading) return <LoadingSpinner />;
        if (perfScorecard.scorecards.length > 0) {
            const vendorName = perfScorecard.data?.vendorName;
            return (
                <div className="min-h-screen bg-gray-50">
                    <Navigation />
                    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                        <AssessmentContextHeader
                            regionName={regionName}
                            orgName={orgName}
                            vendorName={vendorName}
                        />
                        <div className="mb-6 flex items-start justify-between gap-4">
                            <div>
                                <h1 className="text-3xl font-bold text-content-primary">Vendor Performance Review</h1>
                                <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                                    You're the assigned reviewer for {vendorName || "this vendor"}.
                                </p>
                            </div>
                            {perfScorecard.canFill && (
                                <Button onClick={() => setShowPerfReview(true)} className="flex-shrink-0">
                                    Complete Review
                                </Button>
                            )}
                        </div>
                        <VendorPerformanceScorecardTab sc={perfScorecard} canManage={false} />
                    </div>
                    <VendorPerformanceReviewModal
                        isOpen={showPerfReview}
                        onClose={() => setShowPerfReview(false)}
                        sc={perfScorecard}
                        canManage={false}
                    />
                </div>
            );
        }
        return (
            <div className="min-h-screen bg-gray-50">
                <Navigation />
                <div className="max-w-2xl mx-auto py-20 px-4 text-center">
                    <div className="w-14 h-14 mx-auto rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center mb-4">
                        <Shield className="h-6 w-6 text-gray-400" />
                    </div>
                    <h1 className="text-xl font-bold text-content-primary mb-2">You don't have access to this vendor</h1>
                    <p className="text-sm text-gray-500 max-w-md mx-auto">
                        The vendor dashboard is available to organization-level risk managers and to users
                        whose deal or business unit this vendor is associated with. Risks assigned to you
                        are still available in My Tasks.
                    </p>
                    <div className="mt-6">
                        <Button variant="secondary" onClick={() => navigate("/my-tasks")}>
                            Go to My Tasks
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Navigation />
            <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                {/* Context info banner */}
                <AssessmentContextHeader
                    regionName={regionName}
                    orgName={orgName}
                    vendorName={vendor?.vendorName}
                />

                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center gap-3">
                        <h1 className="text-3xl font-bold text-content-primary">
                            Vendor Dashboard
                        </h1>
                        {vendor?.vendorTier && (
                            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                                vendor.vendorTier === 1 ? "bg-red-100 text-red-800" :
                                vendor.vendorTier === 2 ? "bg-yellow-100 text-yellow-800" :
                                "bg-green-100 text-green-800"
                            }`}>
                                Tier {vendor.vendorTier} - {vendor.vendorTier === 1 ? "Critical" : vendor.vendorTier === 2 ? "Important" : "Standard"}
                            </span>
                        )}
                        <VendorAnomalyBadge vendorId={vendorId} onAnomalyData={handleAnomalyData} />
                    </div>
                    <div className="flex items-center gap-4 mt-2">
                        <p className="text-gray-600">
                            {countActiveRisks(risks)} active risks
                        </p>
                        {canManageOrganizationRisks() && (
                            <Button size="sm" variant="secondary" onClick={() => setShowClassifyModal(true)}>
                                <Settings className="h-4 w-4 mr-1" />
                                Classify Vendor
                            </Button>
                        )}
                        {canManageOrganizationRisks() && (
                            <Button size="sm" variant="secondary" onClick={() => setShowWhatIf(true)}>
                                <FlaskConical className="h-4 w-4 mr-1" />
                                What-If Explorer
                            </Button>
                        )}
                        {perfScorecard.canFill && (
                            <Button size="sm" variant="secondary" onClick={() => setShowPerfReview(true)}>
                                <ClipboardList className="h-4 w-4 mr-1" />
                                Score Vendor Performance
                            </Button>
                        )}
                    </div>
                </div>

                {/* AI Intelligence Narrative — persistent above chart */}
                {vendorId && <VendorAiNarrative vendorId={vendorId} financialSummary={financialSummary} mcData={mcData} />}

                {/* Control Effectiveness + Residual Risk — same shared cards (and same v2 engine
                    numbers) as the TPRM assessment risks page, and in the same position: at the
                    top of the page, above the score-card grid, matching the other modules. */}
                {analytics && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <ControlEffectivenessCard analytics={analytics} />
                        <ResidualRiskCard analytics={analytics} />
                    </div>
                )}

                {/* Analytics Cards */}
                {analytics && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        {/* Overall Risk Score */}
                        <Card className="text-center">
                            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">Vendor Risk Score</h3>
                            <div className="w-full flex justify-center mb-2">
                                <ReactSpeedometer
                                    maxValue={100}
                                    value={analytics.overall_risk_score}
                                    needleColor="#1f2937"
                                    customSegmentStops={RISK_GAUGE_SEGMENT_STOPS}
                                    segmentColors={RISK_GAUGE_SEGMENT_COLORS}
                                    segments={RISK_GAUGE_SEGMENT_COLORS.length}
                                    width={250}
                                    height={150}
                                    ringWidth={25}
                                    needleTransitionDuration={1000}
                                    needleTransition="easeElastic"
                                    currentValueText={`${analytics.overall_risk_score}%`}
                                    needleHeightRatio={0.5}
                                />
                            </div>
                            <div className="flex justify-center space-x-2 mb-2">
                                <div className={`text-xs px-2 py-1 rounded ${
                                    analytics.overall_RiskLevel === "CRITICAL" ? "text-red-700 bg-red-100" :
                                    analytics.overall_RiskLevel === "HIGH" ? "text-orange-600 bg-orange-100" :
                                    analytics.overall_RiskLevel === "MEDIUM" ? "text-yellow-600 bg-yellow-100" :
                                    "text-green-600 bg-green-100"
                                }`}>
                                    {analytics.overall_RiskLevel}
                                </div>
                            </div>
                            <div className="text-xs text-gray-500 relative group/totalrisks cursor-default">
                                {analytics.active_risks} Active Risks
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover/totalrisks:block z-20 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg pointer-events-none text-left leading-relaxed">
                                    Composite score across all vendor-linked risk types: TPRM, Enterprise, Dark Web, Ransomware, AI, etc. For TPRM-only risk vector scoring see the panel to the right.
                                </div>
                            </div>
                            <RiskScoreFootnote />
                        </Card>

                        {/* Risk Vectors */}
                        <Card>
                            <div className="mb-3 text-center">
                                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Risk Vectors</h3>
                                <div className="mt-0.5 text-xs text-gray-400">
                                    {vectorFilter
                                        ? <span className="text-indigo-600 font-medium">Filtering: {FAIR_RISK_TYPE_INFO[vectorFilter]?.name || vectorFilter}</span>
                                        : 'Click a bar to filter risks'}
                                </div>
                            </div>
                            {riskVectorsLoading ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                </div>
                            ) : !riskVectors || Object.keys(riskVectors).length === 0 ? (
                                <div className="text-xs text-gray-400 italic py-6 text-center">No risk vector data available</div>
                            ) : (() => {
                                const VECTOR_LIMIT = 5;
                                // Normalise: backend returns {score, count} per vector
                                const entries = Object.entries(riskVectors)
                                    .map(([vec, val]) => [vec, typeof val === 'object' && val !== null ? val : { score: val, count: null }])
                                    .sort(([, a], [, b]) => b.score - a.score);
                                const getVectorColor = getComplianceTextColor;   // shared bands (20% = yellow), matches other Risk Vector lists
                                const getBarColor   = getComplianceBarColor;
                                // Bar width is count-proportional so categories with fewer risks don't
                                // fill the bar just because they happen to be all-CRITICAL severity.
                                // Build EAL lookup from financialSummary if available
                                const totalEAL = financialSummary?.totalExpectedAnnualLoss || 0;
                                const ealByVector = {};
                                if (financialSummary?.riskTypeBreakdown?.length > 0 && totalEAL > 0) {
                                    financialSummary.riskTypeBreakdown.forEach(rt => {
                                        ealByVector[rt.riskType] = rt.expectedAnnualLoss;
                                    });
                                }
                                return (
                                    <>
                                        {entries.length > 0 && entries.every(([, v]) => v.score === 100) && (
                                            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1 mb-2 text-center">
                                                All active findings in each category are rated Critical
                                            </div>
                                        )}
                                        <div className="space-y-1">
                                            {entries.slice(0, showAllVectors ? undefined : VECTOR_LIMIT).map(([vector, val]) => {
                                                const { score } = val;
                                                const isActive = vectorFilter === vector;
                                                return (
                                                    <div
                                                        key={vector}
                                                        onClick={() => {
                                                            setVectorFilter(prev => prev === vector ? "" : vector);
                                                            setDashboardTab("risks");
                                                        }}
                                                        className={`text-xs rounded-lg px-2 py-1.5 cursor-pointer transition-colors ${isActive ? "bg-indigo-50 ring-1 ring-indigo-300" : "hover:bg-gray-50"}`}
                                                        title={`Click to filter risks by ${FAIR_RISK_TYPE_INFO[vector]?.name || vector}`}
                                                    >
                                                        <div className="flex justify-between items-center mb-1 gap-2">
                                                            <span className={`font-medium ${isActive ? "text-indigo-700" : "text-gray-700"}`}>
                                                                {FAIR_RISK_TYPE_INFO[vector]?.name || vector}
                                                            </span>
                                                            <span className={`font-medium shrink-0 ${getVectorColor(score)}`}>{score}%</span>
                                                        </div>
                                                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                                                            <div className={`h-1.5 rounded-full ${getBarColor(score)}`} style={{ width: `${score}%` }} />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {entries.length > VECTOR_LIMIT && (
                                            <button
                                                onClick={() => setShowAllVectors(prev => !prev)}
                                                className="mt-3 w-full text-xs font-medium text-indigo-600 hover:text-indigo-800 flex items-center justify-center gap-1 py-1 rounded hover:bg-indigo-50 transition-colors"
                                            >
                                                {showAllVectors ? <>&#9650; Show less</> : <>&#9660; +{entries.length - VECTOR_LIMIT} more</>}
                                            </button>
                                        )}
                                    </>
                                );
                            })()}
                        </Card>

                        {/* Vulnerability Distribution stacked over the Risk Sources breakdown;
                            the other cards in this row stretch to span both. */}
                        <div className="flex flex-col gap-4">
                            <VulnerabilityDistributionCard analytics={analytics} />
                            <RiskSourceBreakdownCard sources={riskSources} />
                        </div>

                        {/* Predictive Risk Timeline — shared card, consistent with the assessment dashboards */}
                        <PredictiveRiskBars data={predictiveRisk} loading={loadingPredictive} analytics={analytics} />
                    </div>
                )}


                {/* Vendor Impact Metrics (AVI/RPI) — persistent above tabs */}
                {vendorId && <VendorImpactMetrics vendorId={vendorId} anomalyData={anomalyData} financialSummary={financialSummary} deteriorationForecast={deteriorationForecast} activeRiskCount={activeRisks.length} mcData={mcData} loading={mcLoading} recalculating={mcRecalculating} onRecalculate={handleRecalculateMc} />}

                {/* Framework Risk Impact Summary */}
                {frameworkReadiness && frameworkReadiness.totalControlsImpacted > 0 && (
                    <div className="mb-6">
                        <Card className="border-l-4 border-l-emerald-500">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-sm font-semibold text-emerald-700 uppercase tracking-wide mb-1">
                                        Framework Risk Impact
                                    </h3>
                                    <p className="text-xs text-gray-400 mb-1">Remediation progress across all vendor risks</p>
                                    <p className="text-xs text-gray-500">
                                        {frameworkReadiness.totalControlsAddressed} of {frameworkReadiness.totalControlsImpacted} impacted controls addressed through completed remediations
                                    </p>
                                    {frameworkReadiness.totalRisks > 0 && (
                                        <div className="mt-1">
                                            <p className="text-xs text-gray-500">
                                                {frameworkReadiness.risksRemediated} of {frameworkReadiness.totalRisks} risks remediated
                                            </p>
                                            <div className="mt-1 w-48 bg-gray-200 rounded-full h-1.5">
                                                <div
                                                    className="bg-emerald-500 h-1.5 rounded-full transition-all"
                                                    style={{ width: `${Math.round((frameworkReadiness.risksRemediated / frameworkReadiness.totalRisks) * 100)}%` }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="text-right">
                                    <div className="text-2xl font-bold text-emerald-700">
                                        {frameworkReadiness.overallReadinessPercent}%
                                    </div>
                                    <div className="text-xs text-gray-500">addressed</div>
                                </div>
                            </div>
                            {frameworkReadiness.frameworks?.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {frameworkReadiness.frameworks.map((fw) => (
                                        <span
                                            key={fw.framework}
                                            className={`px-2 py-1 rounded text-xs font-medium ${
                                                fw.readinessPercent === 100
                                                    ? "bg-emerald-100 text-emerald-800"
                                                    : (fw.readinessPercent > 0 || fw.risksRemediated > 0)
                                                    ? "bg-amber-100 text-amber-800"
                                                    : "bg-gray-100 text-gray-600"
                                            }`}
                                        >
                                            {fw.framework}: {fw.riskCount} risk{fw.riskCount !== 1 ? 's' : ''} · {fw.risksRemediated} fixed
                                        </span>
                                    ))}
                                </div>
                            )}
                        </Card>
                    </div>
                )}

                {/* Single Intervention Banner — persistent above tabs */}
                {vendorId && <VendorSingleIntervention vendorId={vendorId} financialSummary={financialSummary} />}

                {/* Dashboard Tabs — Chrome style matching Ransomware tabs */}
                <div style={{ background: 'rgb(220,242,248)', borderBottom: '1px solid #bae6fd', paddingLeft: 4, paddingTop: 8, display: 'flex', gap: 2, marginBottom: 24 }}>
                    {[
                        { id: "risks",       label: "Active Risks" },
                        { id: "score-trend", label: "Score Trend" },
                        { id: "financial",   label: "Financial Impact" },
                        { id: "performance", label: "Performance" },
                        { id: "ai-risk",     label: "AI Risk Posture" },
                        false && { id: "controls", label: "Control Mapping" },
                        { id: "threats",     label: "External Threats" },
                        { id: "documents",   label: "Documents" },
                        false && { id: "narrative", label: "AI Narrative" },
                    ].filter(Boolean).map(tab => {
                        const isActive = dashboardTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => {
                                    setDashboardTab(tab.id);
                                    if (tab.id === "risks") { setRiskTypeFilter(""); setVectorFilter(""); }
                                }}
                                style={{
                                    padding: '8px 24px',
                                    fontSize: 13,
                                    fontWeight: isActive ? 600 : 400,
                                    color: isActive ? '#0c4a6e' : '#0369a1',
                                    background: isActive ? '#f9fafb' : 'transparent',
                                    border: isActive ? '1px solid #7dd3fc' : '1px solid transparent',
                                    borderBottom: isActive ? '1px solid #f9fafb' : '1px solid transparent',
                                    borderRadius: '8px 8px 0 0',
                                    cursor: 'pointer',
                                    marginBottom: isActive ? -1 : 0,
                                    position: 'relative',
                                    zIndex: isActive ? 1 : 0,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* Score Trend Tab */}
                {dashboardTab === "score-trend" && (
                    <div>
                        <p className="text-sm text-gray-500 mb-4">Track this vendor's risk score over time to identify trends and measure the impact of remediation efforts. Scores are recalculated as new assessments are completed and risks are resolved or accepted.</p>

                        {/* Predictive Risk summary — always shown when forecast data is available */}
                        {deteriorationForecast && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                {/* Trend + Projections */}
                                <Card>
                                    <h3
                                        className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1 cursor-help"
                                        title={TRAJECTORY_TOOLTIP}
                                    >
                                        AI Risk Trajectory
                                        <span className="text-gray-400 normal-case" aria-hidden="true">ⓘ</span>
                                    </h3>
                                    <div className="space-y-2">
                                        {deteriorationForecast.currentScoreConfidenceLow != null && (
                                            <div className="flex items-center justify-between cursor-help" title={CURRENT_SCORE_CI_TOOLTIP}>
                                                <span className="text-xs text-gray-500">Current Score</span>
                                                <span className="text-xs font-semibold text-gray-700">
                                                    {deteriorationForecast.currentRiskScore}%
                                                    <span className="font-normal text-gray-400 ml-1">
                                                        (est. {deteriorationForecast.currentScoreConfidenceLow}%–{deteriorationForecast.currentScoreConfidenceHigh}%)
                                                    </span>
                                                </span>
                                            </div>
                                        )}
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-gray-500">Trend</span>
                                            <span className={`text-xs font-semibold ${
                                                deteriorationForecast.trendDirection === "rapidly_deteriorating" ? "text-red-600" :
                                                deteriorationForecast.trendDirection === "deteriorating"         ? "text-orange-500" :
                                                deteriorationForecast.trendDirection === "improving"             ? "text-green-600" : "text-gray-500"
                                            }`}>
                                                {deteriorationForecast.trendDirection === "rapidly_deteriorating" ? "↑↑ Rapidly Worsening" :
                                                 deteriorationForecast.trendDirection === "deteriorating"         ? "↑ Worsening" :
                                                 deteriorationForecast.trendDirection === "improving"             ? "↓ Improving" : "→ Stable"}
                                            </span>
                                        </div>
                                        {deteriorationForecast.score90Day != null ? (
                                            deteriorationForecast.daysToMaxRisk != null ? (
                                                // All projections hit 100% ceiling — show days to max instead
                                                (<div className="pt-1" title={TRAJECTORY_TOOLTIP}>
                                                    <div className="text-xs text-red-600 font-semibold mb-1">
                                                        Model-maximum score ({RISK_SCORE_MODEL_MAX}%) projected in ~{deteriorationForecast.daysToMaxRisk} days
                                                    </div>
                                                    <div className="flex items-center justify-between text-xs text-gray-400">
                                                        <span>At current rate ({(deteriorationForecast.trendSlope * 30).toFixed != null
                                                            ? `+${Math.round(deteriorationForecast.trendSlope * 30)} pts/mo`
                                                            : ""})</span>
                                                        <span className="font-medium text-red-500">{RISK_SCORE_MODEL_MAX}%</span>
                                                    </div>
                                                    {deteriorationForecast.confidenceBandLow != null && (
                                                        <div className="flex items-center justify-between pt-1 border-t border-gray-100 mt-1">
                                                            <span className="text-xs text-gray-400">90d Range</span>
                                                            <span className="text-xs font-medium text-gray-500">
                                                                {deteriorationForecast.confidenceBandLow}%–{deteriorationForecast.confidenceBandHigh}%
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>)
                                            ) : (
                                                <>
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs text-gray-500">45-Day</span>
                                                        <span className="text-sm font-bold text-purple-600">{deteriorationForecast.score45Day ?? "—"}%</span>
                                                    </div>
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs text-gray-500">90-Day</span>
                                                        <span className="text-sm font-bold text-purple-600">{deteriorationForecast.score90Day}%</span>
                                                    </div>
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs text-gray-500">180-Day</span>
                                                        <span className="text-sm font-bold text-purple-600">{deteriorationForecast.score180Day ?? "—"}%</span>
                                                    </div>
                                                    {deteriorationForecast.confidenceBandLow != null && (
                                                        <div className="flex items-center justify-between pt-1 border-t border-gray-100">
                                                            <span className="text-xs text-gray-400">90d Range</span>
                                                            <span className="text-xs font-medium text-gray-500">
                                                                {deteriorationForecast.confidenceBandLow}%–{deteriorationForecast.confidenceBandHigh}%
                                                            </span>
                                                        </div>
                                                    )}
                                                </>
                                            )
                                        ) : (
                                            <div className="text-xs text-gray-400 italic pt-1">Insufficient history — need 7+ daily snapshots</div>
                                        )}
                                    </div>
                                </Card>

                                {/* Incident Probability */}
                                {(() => {
                                    const band = getIncidentBand(deteriorationForecast.probabilityOfMaterialIncident);
                                    return (
                                <Card>
                                    <h3
                                        className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1 cursor-help"
                                        title={INCIDENT_BAND_TOOLTIP}
                                    >
                                        90-Day Incident Risk
                                        <span className="text-gray-400 normal-case" aria-hidden="true">ⓘ</span>
                                    </h3>
                                    <div className="text-center py-2" title={INCIDENT_BAND_TOOLTIP}>
                                        <div className={`text-4xl font-bold mb-1 ${band.text}`}>
                                            {deteriorationForecast.probabilityOfMaterialIncident}%
                                        </div>
                                        <div className={`text-xs font-semibold px-3 py-0.5 rounded-full inline-block ${band.badge}`}>
                                            {band.label} Incident Likelihood
                                        </div>
                                        <div className="w-full bg-gray-100 rounded-full h-2 mt-3">
                                            <div
                                                className={`h-2 rounded-full transition-all ${band.bar}`}
                                                style={{ width: `${Math.min(deteriorationForecast.probabilityOfMaterialIncident, 100)}%` }}
                                            />
                                        </div>
                                    </div>
                                </Card>
                                    );
                                })()}

                                {/* Driving Factors */}
                                <Card>
                                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">Driving Factors</h3>
                                    {deteriorationForecast.drivingFactors?.length > 0 ? (
                                        <ul className="space-y-1.5">
                                            {deteriorationForecast.drivingFactors.map((f, i) => (
                                                <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                                                    <span className="text-amber-500 mt-0.5 flex-shrink-0">•</span>
                                                    {f}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <div className="text-xs text-gray-400 italic">No significant risk drivers detected</div>
                                    )}
                                    <div className="mt-3 pt-2 border-t border-gray-100 text-xs text-gray-400">
                                        Based on {deteriorationForecast.dataPointsUsed} daily snapshots
                                    </div>
                                </Card>
                            </div>
                        )}

                        <VendorScoreTrend vendorId={vendorId} forecast={deteriorationForecast} />
                    </div>
                )}

                {/* AI Risk Posture Tab */}
                {dashboardTab === "ai-risk" && (
                    <div>
                        <p className="text-sm text-gray-500 mb-4">AI-generated analysis of this vendor's risk posture across key security domains, based on assessment findings and threat intelligence. Results are broken down by severity, mapped to security frameworks, and categorized by risk domain. This analysis is updated automatically when new assessments are completed.</p>
                        <VendorAiRiskModule vendorId={vendorId} />
                    </div>
                )}

                {/* Financial Impact Tab */}
                {dashboardTab === "financial" && (
                    <Card className="mb-6">
                        <h3 className="text-lg font-semibold mb-4">Vendor Financial Summary</h3>
                        <p className="text-sm text-gray-500 mb-4">This dashboard shows your organization's projected financial exposure from this vendor's security posture — losses your organization would absorb if their findings are exploited, because you remain accountable for vendor-held data, dependent on their operations, and responsible in the eyes of customers and regulators. Estimates use the FAIR (Factor Analysis of Information Risk) methodology, calibrated to your industry, size, and regulatory environment.{" "}<button onClick={() => setShowFinancialMethodology(true)} className="text-indigo-600 hover:text-indigo-800 underline">How these metrics are calculated</button></p>
                        {financialSummary?.importanceBreakdown?.hasMissingInputs && (
                            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                                <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                                <div className="text-sm text-amber-800">
                                    <span className="font-medium">Incomplete vendor classification.</span>{" "}
                                    Complete the Data Sensitivity, Access Scope, and Revenue Dependency fields in vendor settings for more accurate financial impact estimates.
                                </div>
                            </div>
                        )}
                        {financialSummary && financialSummary.totalExpectedAnnualLoss > 0 ? (
                            <div>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                                    <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200 text-center group relative">
                                        <div className="text-xs font-semibold text-emerald-600 uppercase mb-1">Base Case EAL</div>
                                        <div className="text-2xl font-bold text-emerald-800">{formatImpact(financialSummary.totalExpectedAnnualLoss)}</div>
                                        <div className="text-xs text-gray-400 mt-0.5">FAIR-aligned estimate (AI-informed inputs)</div>
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10 w-72 p-2 bg-gray-800 text-white text-xs rounded shadow-lg text-left pointer-events-none leading-relaxed">
                                            FAIR-aligned point estimate (AI-informed inputs) — each finding's SLE × ARO, summed; the most-likely single-year outcome with no simulation. The MC Mean EAL (AVI panel) is the mean of 5,000 simulated years on the same importance-weighted basis; it can come out higher (live threat-intelligence probability boosts, a right-tailed loss distribution) or lower (sampling across each input's range) than this point estimate.
                                        </div>
                                    </div>
                                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 text-center">
                                        <div className="text-xs font-semibold text-blue-600 uppercase mb-1">Maximum Exposure</div>
                                        <div className="text-2xl font-bold text-blue-800">{formatImpact(financialSummary.maximumExposure)}+</div>
                                        <div className="text-xs text-gray-400 mt-0.5">Your ceiling if everything goes wrong at once</div>
                                    </div>
                                    {financialSummary.importanceMultiplier != null && (
                                        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 text-center group relative">
                                            <div className="text-xs font-semibold text-purple-600 uppercase mb-1">Vendor Importance</div>
                                            <div className="text-2xl font-bold text-purple-800">{financialSummary.importanceMultiplier.toFixed(2)}</div>
                                            <div className="text-xs text-gray-400 mt-0.5">
                                                {financialSummary.importanceBreakdown?.hasMissingInputs
                                                    ? "Classification incomplete — using industry averages"
                                                    : "How critical this vendor is to your business"}
                                            </div>
                                            {financialSummary.importanceBreakdown && (
                                                <div className="absolute z-10 hidden group-hover:block left-0 right-0 mt-2 p-3 bg-white border border-gray-200 rounded-lg shadow-lg text-left text-xs">
                                                    <div className="font-semibold text-gray-700 mb-2">Importance Breakdown</div>
                                                    <div className="space-y-1">
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-500">Data Sensitivity:</span>
                                                            <span className="font-medium">{financialSummary.importanceBreakdown.dataSensitivity || "Unknown"} ({financialSummary.importanceBreakdown.dataSensitivityWeight})</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-500">Access Scope:</span>
                                                            <span className="font-medium">{financialSummary.importanceBreakdown.accessScope || "Unknown"} ({financialSummary.importanceBreakdown.accessScopeWeight})</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-500">Revenue Dep.:</span>
                                                            <span className="font-medium">{financialSummary.importanceBreakdown.revenueDependency || "Unknown"} ({financialSummary.importanceBreakdown.revenueDependencyWeight})</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center">
                                        <div className="text-xs font-semibold text-gray-600 uppercase mb-1">Scenario Bands</div>
                                        <div className="text-sm space-y-1">
                                            <div><span className="text-gray-500">Conservative:</span> <span className="font-medium">{formatImpact(financialSummary.scenarioBands?.conservative)}</span></div>
                                            <div><span className="text-gray-500">Base Case:</span> <span className="font-medium">{formatImpact(financialSummary.scenarioBands?.baseCase)}</span></div>
                                            <div><span className="text-gray-500">Severe:</span> <span className="font-bold text-red-700">{formatImpact(financialSummary.scenarioBands?.severe)}+</span></div>
                                        </div>
                                        <div className="text-xs text-gray-400 mt-1">Planning ranges: lucky, expected, stress-test</div>
                                    </div>
                                </div>
                                {crisisNarrative?.length > 0 && (
                                    <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200">
                                        <h4 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
                                            <AlertTriangle className="h-4 w-4" />
                                            Crisis Narrative
                                        </h4>
                                        <div className="space-y-3">
                                            {crisisNarrative.map((para, i) => (
                                                <p key={i} className="text-sm text-gray-700 leading-relaxed">{para}</p>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {financialSummary.riskTypeBreakdown?.length > 0 && (
                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Risk Type Contribution</h4>
                                        <div className="space-y-2">
                                            {financialSummary.riskTypeBreakdown.map(rt => {
                                                const pct = financialSummary.totalExpectedAnnualLoss > 0
                                                    ? (rt.expectedAnnualLoss / financialSummary.totalExpectedAnnualLoss * 100)
                                                    : 0;
                                                return (
                                                    <div key={rt.riskType} className="flex items-center gap-3 group relative cursor-default">
                                                        <span className="text-xs font-medium w-16">{RISK_TYPE_LABELS[rt.riskType] || rt.riskType}</span>
                                                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                                                            <div className="bg-emerald-500 h-2 rounded-full" style={{ width: `${pct}%` }}></div>
                                                        </div>
                                                        <span className="text-xs font-medium w-20 text-right">{formatImpact(rt.expectedAnnualLoss)}</span>
                                                        <span className="text-xs text-gray-500 w-12 text-right">{pct.toFixed(0)}%</span>
                                                        {FAIR_RISK_TYPE_INFO[rt.riskType] && (
                                                            <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block z-10 p-2 bg-gray-800 text-white text-xs rounded shadow-lg w-64">
                                                                <div className="font-semibold">{FAIR_RISK_TYPE_INFO[rt.riskType].name}</div>
                                                                <div className="mt-1 text-gray-300">{FAIR_RISK_TYPE_INFO[rt.riskType].description}</div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p className="text-gray-500 text-sm">No financial impact data available for this vendor.</p>
                        )}
                        <Modal isOpen={showFinancialMethodology} onClose={() => setShowFinancialMethodology(false)} title="How These Metrics Are Calculated" size="3xl">
                            <div className="space-y-5 text-sm text-gray-700">
                                <div>
                                    <h3 className="text-base font-semibold text-gray-900 mb-1">Overview</h3>
                                    <p>
                                        The Vendor Financial Summary uses the <strong>FAIR (Factor Analysis of Information Risk)</strong> framework to estimate the financial exposure your organization faces from this vendor's risk findings. Each metric below is derived from FAIR-Aligned loss tables, adjusted for your organization's size, industry, and regulatory environment.
                                    </p>
                                </div>

                                <div>
                                    <h3 className="text-base font-semibold text-gray-900 mb-2">Key Metrics</h3>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left border border-gray-200 rounded">
                                            <thead>
                                                <tr className="bg-gray-50">
                                                    <th className="px-3 py-2 font-semibold text-gray-900 border-b border-gray-200">Metric</th>
                                                    <th className="px-3 py-2 font-semibold text-gray-900 border-b border-gray-200">What It Tells You</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                <tr>
                                                    <td className="px-3 py-2 font-medium text-emerald-700">Base Case EAL</td>
                                                    <td className="px-3 py-2">The FAIR-Aligned point estimate — each finding's SLE × ARO, summed. Uses most-likely values; no simulation. The MC Mean EAL (AVI panel) is on the same importance-weighted basis and can come out either side of it: live threat-intelligence probability boosts and the right tail of the loss distribution — rare but severe events (major breach, full ransomware shutdown) — pull the simulated mean up, while sampling across each input's range rather than its most-likely value can pull it down.</td>
                                                </tr>
                                                <tr>
                                                    <td className="px-3 py-2 font-medium text-blue-700">Maximum Exposure</td>
                                                    <td className="px-3 py-2">The worst-case total if every identified risk materialized simultaneously in a single year. This upper bound helps size insurance coverage and set risk appetite thresholds.</td>
                                                </tr>
                                                <tr>
                                                    <td className="px-3 py-2 font-medium text-purple-700">Vendor Importance</td>
                                                    <td className="px-3 py-2">A multiplier (0.25 – 2.0) that scales raw loss estimates based on how critical this vendor is to your organization. It is computed from three factors configured in vendor settings:
                                                        <ul className="list-disc list-inside ml-3 mt-1 space-y-0.5 text-gray-600">
                                                            <li><strong>Data Sensitivity</strong> — the classification of data this vendor can access or store (e.g., public, confidential, regulated).</li>
                                                            <li><strong>Access Scope</strong> — how deeply the vendor is integrated into your environment (e.g., no access, limited, privileged).</li>
                                                            <li><strong>Revenue Dependency</strong> — what share of your revenue relies on this vendor's services (e.g., minimal, moderate, critical).</li>
                                                        </ul>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="px-3 py-2 font-medium text-gray-700">Scenario Bands</td>
                                                    <td className="px-3 py-2">Three loss projections that bracket the range of possible outcomes:
                                                        <ul className="list-disc list-inside ml-3 mt-1 space-y-0.5 text-gray-600">
                                                            <li><strong>Conservative</strong> — a lower-bound estimate assuming favorable conditions and partial risk materialization.</li>
                                                            <li><strong>Base Case</strong> — the expected scenario, aligned with the Expected Annual Loss.</li>
                                                            <li><strong>Severe</strong> — a stress-test scenario assuming multiple risks materialize concurrently, useful for board-level reporting and insurance sizing.</li>
                                                        </ul>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-base font-semibold text-gray-900 mb-1">Improving Accuracy</h3>
                                    <p>
                                        Financial estimates are most accurate when vendor classification fields are complete. Navigate to vendor settings and fill in <strong>Data Sensitivity</strong>, <strong>Access Scope</strong>, and <strong>Revenue Dependency</strong> to ensure the Vendor Importance multiplier reflects your actual relationship with this vendor. Without these inputs, a default middle-ground multiplier is applied.
                                    </p>
                                </div>
                            </div>
                        </Modal>
                    </Card>
                )}

                {/* Control Mapping Tab */}
                {dashboardTab === "controls" && (
                    <div>
                        <p className="text-sm text-gray-500 mb-4">Maps this vendor's identified risks to security control frameworks, showing which controls are impacted and where gaps exist. Use this view to understand your vendor's compliance posture and identify areas that need strengthening. Controls are mapped automatically based on assessment findings and risk classifications.</p>
                        <VendorControlMapping vendorId={vendorId} />
                    </div>
                )}

                {/* External Threats Tab */}
                {dashboardTab === "threats" && (
                    <VendorExternalThreats vendorId={vendorId} />
                )}

                {/* Documents Tab (UN EOI Phase 5: registry + review-date reminders).
                    canCreateVendor mirrors the backend CanManageVendor gate (org-level or
                    scoped RISK_MANAGER+), matching the vendor-edit permission. */}
                {dashboardTab === "documents" && (
                    <VendorDocumentsTab vendorId={vendorId} canManage={canCreateVendor()} />
                )}

                {/* Performance Scorecard Tab (business-owner review) */}
                {dashboardTab === "performance" && (
                    <VendorPerformanceScorecardTab
                        sc={perfScorecard}
                        canManage={canManagePerfScorecard}
                    />
                )}

                {/* AI Narrative Tab */}
                {dashboardTab === "narrative" && (
                    <VendorAiNarrative vendorId={vendorId} financialSummary={financialSummary} mcData={mcData} />
                )}

                {/* Risks Tab */}
                {dashboardTab === "risks" && risks.length === 0 ? (
                    <div>
                        <p className="text-sm text-gray-500 mb-4">A consolidated view of all active risks identified for this vendor, including findings from document assessments, live Dark Web exposure monitoring, and attack surface scans. Each risk includes severity, financial impact estimates, and remediation status. Expand any risk to view detailed findings, impact analysis, and activity history.</p>
                        <Card>
                            <div className="text-center py-12 text-gray-500">
                                <Shield className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                                <p>No risks found for this vendor</p>
                            </div>
                        </Card>
                    </div>
                ) : dashboardTab === "risks" ? (
                    <>
                        <p className="text-sm text-gray-500 mb-4">A consolidated view of all active risks identified for this vendor, including findings from document assessments, live Dark Web exposure monitoring, and attack surface scans. Each risk includes severity, financial impact estimates, and remediation status. Expand any risk to view detailed findings, impact analysis, and activity history.</p>
                        {selectedRiskIds.size > 0 && (
                            <div className={`sticky ${PINNED_PANEL_TOP} z-20 flex items-center gap-3 mb-3 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-lg shadow-sm`}>
                                <span className="text-sm font-medium text-blue-800">
                                    {selectedRiskIds.size} risk{selectedRiskIds.size !== 1 ? 's' : ''} selected
                                </span>
                                <button
                                    onClick={() => setShowBulkAssignModal(true)}
                                    className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 flex items-center gap-1.5"
                                >
                                    <Shield className="h-4 w-4" />
                                    Assign Selected
                                </button>
                                <button onClick={() => setSelectedRiskIds(new Set())} className="text-sm text-blue-600 hover:text-blue-800">Clear</button>
                            </div>
                        )}
                        <div className="mb-4 flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-2 flex-wrap flex-1">
                                {filteredRisks.length > 0 && (
                                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4 text-blue-600 rounded border-gray-300 cursor-pointer"
                                            checked={selectedRiskIds.size === filteredRisks.length && filteredRisks.length > 0}
                                            ref={el => { if (el) el.indeterminate = selectedRiskIds.size > 0 && selectedRiskIds.size < filteredRisks.length; }}
                                            onChange={toggleSelectAll}
                                        />
                                        <span className="text-sm text-gray-600">Select All</span>
                                    </label>
                                )}
                                <span className="text-sm text-gray-600">
                                    Showing {filteredRisks.length} of {risks.length} risks
                                    {(riskTypeFilter || vectorFilter || statusFilter !== "all" || searchTerm) && <span className="ml-1 text-gray-400">(filtered)</span>}
                                </span>
                                {vectorFilter && (
                                    <button
                                        onClick={() => setVectorFilter("")}
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium hover:bg-indigo-200 transition-colors"
                                    >
                                        {FAIR_RISK_TYPE_INFO[vectorFilter]?.name || vectorFilter}
                                        <span className="ml-0.5">✕</span>
                                    </button>
                                )}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search risks..."
                                        aria-label="Search risks"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-9 w-56 border border-gray-300 rounded-md py-1.5 text-sm"
                                    />
                                </div>
                                <select
                                    value={vectorFilter}
                                    onChange={e => setVectorFilter(e.target.value)}
                                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 bg-white"
                                >
                                    <option value="">All Vectors</option>
                                    {Object.keys(riskVectors || {}).sort().map(vec => (
                                        <option key={vec} value={vec}>
                                            {FAIR_RISK_TYPE_INFO[vec]?.name || vec}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    value={riskTypeFilter}
                                    onChange={e => setRiskTypeFilter(e.target.value)}
                                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 bg-white"
                                >
                                    <option value="">All Sources</option>
                                    {Object.keys(riskTypeBreakdown).sort().map(type => (
                                        <option key={type} value={type}>
                                            {RISK_TYPE_LABELS[type] || type} ({riskTypeBreakdown[type]})
                                        </option>
                                    ))}
                                </select>
                                <RiskStatusFilter
                                    value={statusFilter}
                                    onChange={setStatusFilter}
                                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 bg-white"
                                />
                            </div>
                        </div>
                        {filteredRisks.length === 0 ? (
                            <Card>
                                <div className="text-center py-12 text-gray-500">
                                    <Shield className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                                    <p>No risks match the selected filter.</p>
                                    <button
                                        onClick={() => { setRiskTypeFilter(""); setVectorFilter(""); setStatusFilter("all"); setSearchTerm(""); }}
                                        className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium"
                                    >
                                        Clear filters
                                    </button>
                                </div>
                            </Card>
                        ) : (<>
                        <div className="space-y-6" ref={listTopRef}>
                            {pagedRisks.map((risk) => {
                                const severity = (risk.riskSeverity || "MEDIUM").toUpperCase();
                                const weights = { CRITICAL: 100, HIGH: 75, MEDIUM: 50, LOW: 25 };
                                const weightedScore = weights[severity] || 0;
                                const riskType = risk.riskType || "Unknown";

                                return (
                                    <Card key={risk.id} className={`${risk.status === "accepted" ? "bg-gray-50" : ""}`}>
                                        {canManageThisRisk(risk) && (
                                            <input
                                                type="checkbox"
                                                className="h-4 w-4 mb-2 text-blue-600 rounded border-gray-300 cursor-pointer"
                                                checked={selectedRiskIds.has(risk.id)}
                                                onChange={e => { e.stopPropagation(); toggleRiskSelection(risk.id); }}
                                            />
                                        )}
                                        <div className="flex justify-between items-start mb-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mb-1">
                                                    <h3 className="text-lg font-semibold">{risk.riskTitle}</h3>
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className={`px-2 py-1 rounded text-xs font-medium border ${getRiskColor(severity)}`}>
                                                            {severity} ({weightedScore}%)
                                                        </span>
                                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getRiskBadgeColor(riskType)}`}>
                                                            {RISK_TYPE_LABELS[riskType] || riskType}
                                                        </span>
                                                        {risk.status === "accepted" && (
                                                            <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs">
                                                                Accepted
                                                            </span>
                                                        )}
                                                        <RiskStatusBadge risk={risk} className="flex-shrink-0" />
                                                    </div>
                                                </div>

                                                {/* Vector category — separate line below title, clickable to filter */}
                                                {risk.aiRiskType && (() => {
                                                    const code = risk.aiRiskType.trim().toUpperCase() === "DD" ? "DDOS" : risk.aiRiskType.trim().toUpperCase();
                                                    const isVecActive = vectorFilter === code;
                                                    return (
                                                        <div className="flex items-center gap-1.5 mt-0.5">
                                                            <span className="text-xs text-gray-400">Vector:</span>
                                                            <button
                                                                onClick={e => { e.stopPropagation(); setVectorFilter(prev => prev === code ? "" : code); }}
                                                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${isVecActive ? "bg-indigo-100 text-indigo-700 ring-1 ring-indigo-300" : "bg-gray-100 text-gray-600 hover:bg-indigo-50 hover:text-indigo-600"}`}
                                                                title={`Filter by vector: ${FAIR_RISK_TYPE_INFO[code]?.name || code}`}
                                                            >
                                                                {FAIR_RISK_TYPE_INFO[code]?.name || code}
                                                            </button>
                                                        </div>
                                                    );
                                                })()}

                                                {(risk.assignedToUserName) && (
                                                    <div className="text-sm text-gray-700">
                                                        Assigned To:{" "}
                                                        <span className="font-medium text-blue-700">
                                                            {risk.assignedToUserName}
                                                        </span>
                                                    </div>
                                                )}
                                                {risk.dealName && (
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        Deal: {risk.dealName}
                                                    </div>
                                                )}
                                                {risk.businessUnitName && (
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        Business Unit: {risk.businessUnitName}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex items-start gap-2 shrink-0 ml-3">
                                                <div className="text-right">
                                                    {risk.finalCalculatedMonetaryImpact != null ? (
                                                        risk.impactLow != null && risk.impactHigh != null ? (
                                                            <>
                                                                <div className="text-lg font-bold text-emerald-700">
                                                                    {formatImpact(risk.impactLow)} – {formatImpact(risk.impactHigh)}+
                                                                </div>
                                                                <div className="text-xs text-emerald-600">
                                                                    Most Likely: {formatImpact(risk.finalCalculatedMonetaryImpact)}
                                                                </div>
                                                                <div className="text-xs text-gray-400">Engine-Calculated Range</div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div className="text-lg font-bold text-emerald-700">
                                                                    ${Math.round(Number(risk.finalCalculatedMonetaryImpact)).toLocaleString()}
                                                                </div>
                                                                <div className="text-xs text-gray-500">Engine-Calculated Impact</div>
                                                            </>
                                                        )
                                                    ) : null}
                                                </div>
                                                <button
                                                    onClick={() => toggleCard(risk.id)}
                                                    className="flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
                                                    title={expandedCards[risk.id] ? "Collapse details" : "Expand details"}
                                                >
                                                    {expandedCards[risk.id]
                                                        ? <ChevronDown size={18} />
                                                        : <ChevronRight size={18} />
                                                    }
                                                    <span className="text-xs leading-none">{expandedCards[risk.id] ? "Hide" : "Details"}</span>
                                                </button>
                                            </div>
                                        </div>

                                        {/* Security Frameworks — only for tagged Enterprise/AI/GRC risks, never native vendor risks */}
                                        {["Enterprise", "AI", "GRC", "DarkWeb", "Ransomware", "BusinessUnit", "BusinessUnits", "TPRM", "DealApplication", "Deals"].includes(risk.riskType) && Object.keys(risk.frameworkImpact || {}).filter(f => risk.frameworkImpact?.[f] && !isFrameworkNotApplicable(risk.frameworkImpact[f])).length > 0 && (
                                            <div className="flex gap-2 overflow-x-auto pb-1 mb-1">
                                                <h3 className="text-lg font-semibold mr-4 shrink-0">Framework:</h3>
                                                {Object.keys(risk.frameworkImpact || {})
                                                    .filter(f => risk.frameworkImpact?.[f] && !isFrameworkNotApplicable(risk.frameworkImpact[f]))
                                                    .map((framework) => (
                                                        <FrameworkCard
                                                            key={`${risk.id}-framework-${framework}`}
                                                            title={framework}
                                                            controls={risk.frameworkImpact?.[framework] || ""}
                                                            color={getFrameworkColor(framework)}
                                                        />
                                                    ))}
                                            </div>
                                        )}
                                        {["Enterprise", "AI", "GRC", "DarkWeb", "Ransomware", "BusinessUnit", "BusinessUnits", "TPRM", "DealApplication", "Deals"].includes(risk.riskType) && Object.keys(risk.frameworkImpact || {}).filter(f => risk.frameworkImpact?.[f] && !isFrameworkNotApplicable(risk.frameworkImpact[f])).length > 0 && (
                                            <p className="text-[10px] text-gray-400 italic mb-4">
                                                click + on any framework to see all its mapped controls
                                            </p>
                                        )}

                                        {/* Expanded tabs */}
                                        {expandedCards[risk.id] && (() => {
                                            const activeTab = riskActiveTabs[risk.id] || "overview";
                                            const setTab = (tab) => setRiskActiveTabs(prev => ({ ...prev, [risk.id]: tab }));
                                            const tabs = [
                                                { id: "overview", label: "Overview", icon: "\ud83d\udccb" },
                                                { id: "evolution", label: "What May Break", icon: "\ud83d\udcc8" },
                                                { id: "financial", label: "Financial Impact", icon: "\ud83d\udcb0" },
                                                { id: "log", label: "Activity Log", icon: "\ud83d\udd50" },
                                            ];
                                            return (
                                                <>
                                                    <div className="flex items-end gap-0.5 bg-slate-100 border border-gray-200 border-b-0 rounded-t-xl px-2 pt-2">
                                                        {tabs.map(t => {
                                                            const isActive = activeTab === t.id;
                                                            return (
                                                                <button key={t.id} onClick={() => setTab(t.id)}
                                                                    className={`relative flex items-center gap-2 px-5 py-2.5 text-xs font-semibold rounded-t-lg -mb-px transition-all duration-150 focus:outline-none border ${isActive ? "bg-white text-indigo-700 border-gray-200 z-10 shadow-[0_-1px_3px_rgba(0,0,0,0.06)]" : "bg-transparent border-transparent text-gray-500 hover:bg-white/70 hover:text-gray-700"}`}
                                                                    style={isActive ? { borderBottomColor: "#ffffff" } : {}}>
                                                                    <span className="text-sm leading-none">{t.icon}</span>
                                                                    <span>{t.label}</span>
                                                                </button>
                                                            );
                                                        })}
                                                        <div className="flex-1 border-b border-gray-200 self-end h-[1px]" />
                                                    </div>
                                                    <div className="border border-gray-200 border-t-0 rounded-b-xl rounded-tr-xl bg-white p-4 mb-1">
                                                        {activeTab === "overview" && (
                                                            <div>
                                                                <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                                    <h4 className="font-semibold text-gray-700 mb-2">{"\ud83d\udccb"} Risk Description</h4>
                                                                    <p className="text-gray-700 text-sm">{risk.riskDescription || "No description available."}</p>
                                                                </div>
                                                                {risk.dbCrisisNarrative && (
                                                                    <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
                                                                        <h4 className="font-semibold text-red-900 mb-2 flex items-center">{"\u26a0\ufe0f"} Crisis Narrative</h4>
                                                                        <p className="text-gray-700 text-sm">{risk.dbCrisisNarrative}</p>
                                                                    </div>
                                                                )}
                                                                <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                                                    <h4 className="font-semibold text-blue-900 mb-2 flex items-center">{"\u2705"} Resolution & Expected Best Practice</h4>
                                                                    <p className="text-sm text-gray-700">{risk.dbExpectedBestPractice || "No best practice information available"}</p>
                                                                    {(() => {
                                                                        const cleanedSteps = parseRemediationSteps(risk?.dbStepByStepRemediation);
                                                                        if (cleanedSteps) {
                                                                            return (<div className="space-y-1 mt-2">{cleanedSteps.map((s, i) => (<div key={i} className="text-sm text-gray-800">{i + 1}. {s}</div>))}</div>);
                                                                        }
                                                                        return (<p className="text-gray-500 text-sm mt-2">No remediation steps available.</p>);
                                                                    })()}
                                                                </div>
                                                                {risk?.dbAxionRemediationTrigger && (
                                                                    <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                                                                        <h4 className="font-semibold text-purple-900 mb-2 flex items-center">{"\ud83c\udfaf"} Remediation Trigger</h4>
                                                                        <RemediationTriggerList trigger={risk.dbAxionRemediationTrigger} />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {activeTab === "evolution" && (
                                                            <div>
                                                                {(risk.riskEvolutionStory || risk.dbPredictive45Day || risk.dbPredictive90Day || risk.dbPredictive180Day) ? (
                                                                    <div className="p-4 rounded-xl border bg-gradient-to-br from-gray-50 to-gray-100">
                                                                        <h4 className="text-sm font-semibold text-gray-800 mb-3">{"\ud83d\udcc8"} What May Break - Risk Evolution</h4>
                                                                        {risk.riskEvolutionStory && (
                                                                            <div className="mb-3">
                                                                                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">AI Analysis</p>
                                                                                <div className="flex items-start gap-3 p-3 bg-orange-50 border border-yellow-200 rounded-lg">
                                                                                    <p className="text-sm text-gray-700">{risk.riskEvolutionStory}</p>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        <div className="space-y-3 mt-3">
                                                                            {risk.dbPredictive45Day && (
                                                                                <div className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                                                                    <div className="text-yellow-600 text-lg">{"\ud83d\udfe1"}</div>
                                                                                    <div>
                                                                                        <div className="text-xs font-semibold text-yellow-700 uppercase tracking-wide">Day 45 – Early Warning</div>
                                                                                        <p className="text-sm text-gray-700 mt-1">{risk.dbPredictive45Day}</p>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                            {risk.dbPredictive90Day && (
                                                                                <div className="flex items-start gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                                                                    <div className="text-orange-600 text-lg">{"\ud83d\udfe0"}</div>
                                                                                    <div>
                                                                                        <div className="text-xs font-semibold text-orange-700 uppercase tracking-wide">Day 90 – Active Threat Emerging</div>
                                                                                        <p className="text-sm text-gray-700 mt-1">{risk.dbPredictive90Day}</p>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                            {risk.dbPredictive180Day && (
                                                                                <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                                                                    <div className="text-red-600 text-lg">{"\ud83d\udd34"}</div>
                                                                                    <div>
                                                                                        <div className="text-xs font-semibold text-red-700 uppercase tracking-wide">Day 180 – Likely Incident</div>
                                                                                        <p className="text-sm text-gray-700 mt-1">{risk.dbPredictive180Day}</p>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <p className="text-sm text-gray-500 italic">No evolution data available for this risk.</p>
                                                                )}
                                                            </div>
                                                        )}
                                                        {activeTab === "financial" && (
                                                            <div>
                                                                {risk.finalCalculatedMonetaryImpact != null ? (
                                                                    <>
                                                                        <div className="mb-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                                                                            <h4 className="font-medium text-emerald-900 mb-3 flex items-center gap-2 flex-wrap">
                                                                                {"\ud83d\udcb0"} Monetary Impact Analysis
                                                                                {risk.impactLow != null && risk.impactHigh != null ? (
                                                                                    <span className="font-bold text-emerald-700">
                                                                                        {formatImpact(risk.impactLow)} – {formatImpact(risk.impactHigh)}+
                                                                                        <span className="ml-1 text-sm font-normal text-emerald-600">
                                                                                            (Most Likely: {formatImpact(risk.finalCalculatedMonetaryImpact)})
                                                                                        </span>
                                                                                    </span>
                                                                                ) : (
                                                                                    <span className="font-bold text-emerald-700">
                                                                                        ${Math.round(Number(risk.finalCalculatedMonetaryImpact)).toLocaleString()}
                                                                                    </span>
                                                                                )}
                                                                            </h4>
                                                                            {(() => {
                                                                                const baseExp = Number(risk.finalCalculatedMonetaryImpact ?? 0);
                                                                                const baseLow = Number(risk.impactLow ?? risk.finalCalculatedMonetaryImpact ?? 0);
                                                                                const baseHigh = Number(risk.impactHigh ?? risk.finalCalculatedMonetaryImpact ?? 0);
                                                                                const hasBands = risk.impactLow != null && risk.impactHigh != null && baseLow !== baseHigh;
                                                                                const cats = [
                                                                                    { label: "Direct Costs", pct: 0.30, color: "text-red-600", badge: "bg-red-50 border-red-200" },
                                                                                    { label: "Bus. Interruption", pct: 0.25, color: "text-orange-600", badge: "bg-orange-50 border-orange-200" },
                                                                                    { label: "Recovery Costs", pct: 0.20, color: "text-blue-600", badge: "bg-blue-50 border-blue-200" },
                                                                                    { label: "Regulatory Fines", pct: 0.15, color: "text-purple-600", badge: "bg-purple-50 border-purple-200" },
                                                                                    { label: "Reputation Damage", pct: 0.10, color: "text-pink-600", badge: "bg-pink-50 border-pink-200" },
                                                                                ];
                                                                                return (
                                                                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                                                                        {cats.map(({ label, pct, color, badge }) => {
                                                                                            const exp = Math.max(25_000, Math.round(baseExp * pct));
                                                                                            const low = Math.max(25_000, Math.round(baseLow * pct));
                                                                                            const high = Math.max(25_000, Math.round(baseHigh * pct));
                                                                                            return (
                                                                                                <div key={label} className={`text-center p-2 rounded border ${badge}`}>
                                                                                                    <div className="text-xs font-semibold text-gray-600 mb-1">{label}</div>
                                                                                                    {hasBands ? (
                                                                                                        <>
                                                                                                            <div className={`text-sm font-bold ${color} leading-tight`}>{formatImpact(low)} – {formatImpact(high)}+</div>
                                                                                                            <div className="text-xs text-gray-400 mt-0.5">ML: {formatImpact(exp)}</div>
                                                                                                        </>
                                                                                                    ) : (
                                                                                                        <div className={`text-base font-bold ${color}`}>{formatImpact(exp)}</div>
                                                                                                    )}
                                                                                                    <div className="text-xs text-gray-400 mt-0.5">{Math.round(pct * 100)}%</div>
                                                                                                </div>
                                                                                            );
                                                                                        })}
                                                                                    </div>
                                                                                );
                                                                            })()}
                                                                        </div>
                                                                        <FinancialImpactSection risk={risk} />
                                                                    </>
                                                                ) : (
                                                                    <p className="text-sm text-gray-500 italic">No financial impact data available for this risk.</p>
                                                                )}
                                                            </div>
                                                        )}
                                                        {activeTab === "log" && (
                                                            <ActivityLogTab riskId={risk.id} riskType={risk.riskType || "TPRM"} />
                                                        )}
                                                    </div>
                                                </>
                                            );
                                        })()}

                                        {/* Action Buttons */}
                                        <div className="flex justify-end gap-2 mt-4">
                                            {canManageThisRisk(risk) && (
                                                <Button
                                                    size="sm"
                                                    variant="secondary"
                                                    disabled={selectedRiskIds.size > 0}
                                                    title={selectedRiskIds.size > 0 ? 'Clear or assign the current selection first' : undefined}
                                                    onClick={() => handleOpenAssignModal(risk)}
                                                >
                                                    <Shield className="h-4 w-4 mr-2" />
                                                    {risk.assignedToUserName ? "Reassign Risk" : "Assign Risk"}
                                                </Button>
                                            )}
                                        </div>
                                    </Card>
                                );
                            })}
                        </div>

                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            totalItems={filteredRisks.length}
                            pageSize={pageSize}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            scrollTargetRef={listTopRef}
                        />
                        </>)}
                    </>
                ) : null}
            </div>
            {/* Classify Vendor Modal */}
            {showClassifyModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg w-[480px] shadow-xl">
                        <h2 className="text-xl font-semibold mb-4">Classify Vendor</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Data Sensitivity</label>
                                <select value={classifyForm.dataSensitivity} onChange={e => setClassifyForm(p => ({ ...p, dataSensitivity: e.target.value }))}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2">
                                    <option value="">-- Select --</option>
                                    <option value="PHI">PHI</option>
                                    <option value="PII">PII</option>
                                    <option value="Financial">Financial</option>
                                    <option value="Public">Public</option>
                                    <option value="None">None</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Access Scope</label>
                                <select value={classifyForm.accessScope} onChange={e => setClassifyForm(p => ({ ...p, accessScope: e.target.value }))}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2">
                                    <option value="">-- Select --</option>
                                    <option value="NetworkAccess">Network Access</option>
                                    <option value="DataProcessor">Data Processor</option>
                                    <option value="SaaSOnly">SaaS Only</option>
                                    <option value="Physical">Physical Access</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Revenue Dependency</label>
                                <select value={classifyForm.revenueDependency} onChange={e => setClassifyForm(p => ({ ...p, revenueDependency: e.target.value }))}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2">
                                    <option value="">-- Select --</option>
                                    <option value="Critical">Critical</option>
                                    <option value="Important">Important</option>
                                    <option value="Low">Low</option>
                                </select>
                            </div>
                            <div className="border-t border-gray-200 pt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Manual Tier Override (optional)</label>
                                <label className="flex items-center gap-2 mb-3 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={classifyForm.autoCalculate}
                                        onChange={e => setClassifyForm(p => ({
                                            ...p,
                                            autoCalculate: e.target.checked,
                                            vendorTier: e.target.checked ? "" : p.vendorTier,
                                            tierOverrideReason: e.target.checked ? "" : (p.tierOverrideReason === "Auto-calculated" ? "" : p.tierOverrideReason),
                                        }))}
                                        className="h-4 w-4 text-blue-600 rounded border-gray-300"
                                    />
                                    <span className="text-sm font-medium text-gray-700">Auto Calculate</span>
                                </label>
                                <select
                                    value={classifyForm.vendorTier}
                                    onChange={e => setClassifyForm(p => ({ ...p, vendorTier: e.target.value }))}
                                    disabled={classifyForm.autoCalculate}
                                    className={`w-full border border-gray-300 rounded-md px-3 py-2 mb-2 ${classifyForm.autoCalculate ? 'bg-gray-100 cursor-not-allowed text-gray-400' : ''}`}
                                >
                                    <option value="">-- Select Tier --</option>
                                    <option value="1">Tier 1 - Critical</option>
                                    <option value="2">Tier 2 - Important</option>
                                    <option value="3">Tier 3 - Standard</option>
                                </select>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Override Reason
                                        {!classifyForm.autoCalculate && <span className="text-red-500 ml-1">*</span>}
                                    </label>
                                    <input
                                        type="text"
                                        value={classifyForm.tierOverrideReason}
                                        onChange={e => setClassifyForm(p => ({ ...p, tierOverrideReason: e.target.value }))}
                                        placeholder={classifyForm.autoCalculate ? "Not required for auto calculate" : "Reason for manual override..."}
                                        disabled={classifyForm.autoCalculate}
                                        className={`w-full border border-gray-300 rounded-md px-3 py-2 ${classifyForm.autoCalculate ? 'bg-gray-100 cursor-not-allowed text-gray-400' : ''}`}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <Button variant="secondary" onClick={() => setShowClassifyModal(false)}>Cancel</Button>
                            <Button disabled={classifySaving} onClick={async () => {
                                if (!classifyForm.autoCalculate && classifyForm.vendorTier && !classifyForm.tierOverrideReason.trim()) {
                                    alert("Override Reason is required when selecting a manual tier.");
                                    return;
                                }
                                setClassifySaving(true);
                                try {
                                    const payload = {
                                        ...vendor,
                                        dataSensitivity: classifyForm.dataSensitivity || null,
                                        accessScope: classifyForm.accessScope || null,
                                        revenueDependency: classifyForm.revenueDependency || null,
                                        vendorTier: (!classifyForm.autoCalculate && classifyForm.vendorTier) ? Number(classifyForm.vendorTier) : null,
                                        // Auto-calculate → send no tier + no reason so the backend recomputes the tier.
                                        // A non-empty reason here would be read as a manual override and blank the tier.
                                        tierOverrideReason: (!classifyForm.autoCalculate && classifyForm.vendorTier) ? classifyForm.tierOverrideReason : null,
                                    };
                                    await apiCall("PUT", `/TPRMVendor/${vendorId}`, payload);
                                    setShowClassifyModal(false);
                                    setLoading(true);
                                    fetchVendorData();
                                } catch (err) {
                                    alert("Failed to classify vendor.");
                                } finally {
                                    setClassifySaving(false);
                                }
                            }}>
                                {classifySaving ? "Saving..." : "Save Classification"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            {showAssignModal && selectedRisk && (
                <AssignRiskModal
                    show={showAssignModal}
                    risk={selectedRisk}
                    riskId={selectedRisk.id}
                    riskType={selectedRisk.riskType || "TPRM"}
                    assignEndpoint={getRiskAssignEndpoint(selectedRisk)}
                    onClose={() => { setShowAssignModal(false); setSelectedRisk(null); }}
                    refreshData={fetchVendorData}
                    dealId={selectedRisk.dealId || undefined}
                />
            )}
            {showBulkAssignModal && (() => {
                const selected = risks.filter(r => selectedRiskIds.has(r.id));
                // Deal context only when every selected risk belongs to the same deal
                const dealIds = new Set(selected.map(r => r.dealId || null));
                const sharedDealId = dealIds.size === 1 ? [...dealIds][0] || undefined : undefined;
                return (
                    <BulkAssignRiskModal
                        show={showBulkAssignModal}
                        onClose={() => { setShowBulkAssignModal(false); setSelectedRiskIds(new Set()); }}
                        risks={selected}
                        riskType={null}
                        refreshData={fetchVendorData}
                        dealId={sharedDealId}
                    />
                );
            })()}
            <VendorWhatIfExplorer
                vendorId={vendorId}
                isOpen={showWhatIf}
                onClose={() => setShowWhatIf(false)}
                risks={activeRisks}
            />
            <VendorPerformanceReviewModal
                isOpen={showPerfReview}
                onClose={() => setShowPerfReview(false)}
                sc={perfScorecard}
                canManage={canManagePerfScorecard}
            />
        </div>
    );
};

export default VendorDashboard;
